import React, { useState, useRef } from 'react';
import {
  ImageIcon, Save, Loader2, Package, Check,
  ChevronDown, ChevronUp, Sparkles, Share2, Link, Copy, X,
  Camera, Award, Globe
} from 'lucide-react';
import VideoCard from './VideoCard';
import { resolveImageUrl } from '../../utils/imageUrl';

import { BACKEND_URL } from '../../config/api';
const APP_URL = typeof window !== 'undefined' ? window.location.origin : '';

const LIFECYCLE_BADGES = {
  idea: { label: 'Idea', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  finished: { label: 'Finished', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  shared: { label: 'Shared', className: 'bg-teal-100 text-teal-700 border-teal-200' },
};

const ProjectCard = ({
  suggestion,
  onSave,
  onGenerateImage,
  isGeneratingImage,
  stashItemMap,
  onItemClick,
  getAuthHeaders,
  onLifecycleChange
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const finishedPhotoRef = useRef(null);

  if (!suggestion) return null;

  const isShared = suggestion.is_shared && suggestion.share_id;
  const shareUrl = isShared ? `${APP_URL}/shared/${suggestion.share_id}` : '';

  const handleShare = async () => {
    if (isShared) {
      setShowShareMenu(true);
      return;
    }
    setSharing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/design/${suggestion.suggestion_id}/share`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders?.() || {}
      });
      if (res.ok) {
        const data = await res.clone().json();
        suggestion.share_id = data.share_id;
        suggestion.is_shared = true;
        setShowShareMenu(true);
      }
    } catch (err) {
      console.error('Share error:', err);
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/design/${suggestion.suggestion_id}/share`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders?.() || {}
      });
      if (res.ok) {
        suggestion.is_shared = false;
        suggestion.share_id = null;
        setShowShareMenu(false);
      }
    } catch (err) {
      console.error('Unshare error:', err);
    }
  };

  const handleCopyLink = async () => {
    const url = `${APP_URL}/shared/${suggestion.share_id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFinishedPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/design/${suggestion.suggestion_id}/finish`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders?.() || {},
        body: formData,
      });
      if (res.ok) {
        const data = await res.clone().json();
        suggestion.finished_photo_url = data.finished_photo_url;
        suggestion.lifecycle_state = data.lifecycle_state;
        onLifecycleChange?.(suggestion.suggestion_id, data);
      }
    } catch (err) {
      console.error('Finish upload error:', err);
    } finally {
      setUploadingPhoto(false);
      if (finishedPhotoRef.current) finishedPhotoRef.current.value = '';
    }
  };

  const handleRevertToIdea = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/design/${suggestion.suggestion_id}/finish`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders?.() || {},
      });
      if (res.ok) {
        const data = await res.clone().json();
        suggestion.finished_photo_url = null;
        suggestion.lifecycle_state = data.lifecycle_state;
        onLifecycleChange?.(suggestion.suggestion_id, data);
      }
    } catch (err) {
      console.error('Revert error:', err);
    }
  };

  const hasImage = !!suggestion.inspiration_image_url;
  const hasColors = suggestion.color_scheme?.length > 0;
  const hasItems = suggestion.items_needed?.length > 0;
  const hasInstructions = suggestion.assembly_instructions?.length > 0;
  const hasVideos = suggestion.video_recommendations?.length > 0;
  const hasFinishedPhoto = !!suggestion.finished_photo_url;
  const lifecycleState = suggestion.lifecycle_state || 'idea';
  const badge = LIFECYCLE_BADGES[lifecycleState] || LIFECYCLE_BADGES.idea;

  return (
    <div
      className="bg-white rounded-xl border border-beige shadow-sm overflow-hidden"
      data-testid={`project-card-${suggestion.suggestion_id}`}
    >
      {/* Title bar */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge.className}`}
              data-testid={`lifecycle-badge-${lifecycleState}`}
            >
              {badge.label}
            </span>
          </div>
          <h3 className="text-lg font-medium text-charcoal leading-snug">
            {suggestion.title}
          </h3>
        </div>
        <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
          <button
            onClick={handleShare}
            disabled={sharing}
            className={`p-2 rounded-lg transition-colors ${
              isShared
                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                : 'bg-cream text-charcoal hover:bg-sage-light'
            }`}
            title={isShared ? 'Shared — click to manage' : 'Share design'}
            data-testid="share-project-btn"
          >
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
          </button>
          <button
            onClick={() => onSave?.(suggestion.suggestion_id)}
            className={`p-2 rounded-lg transition-colors ${
              suggestion.saved
                ? 'bg-sage text-white'
                : 'bg-cream text-charcoal hover:bg-sage-light'
            }`}
            title={suggestion.saved ? 'Saved' : 'Save design'}
            data-testid="save-project-btn"
          >
            <Save size={16} />
          </button>
        </div>
      </div>

      {/* Share menu */}
      {showShareMenu && isShared && (
        <div className="mx-5 mb-3 p-4 bg-blue-50 border border-blue-200 rounded-lg relative" data-testid="share-menu">
          <button
            onClick={() => setShowShareMenu(false)}
            className="absolute top-2 right-2 p-1 text-gray-soft hover:text-charcoal"
          >
            <X size={14} />
          </button>
          <p className="text-xs text-blue-700 font-medium mb-2">Public Share Link</p>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 text-xs px-3 py-2 border border-blue-200 rounded-lg bg-white text-charcoal"
              data-testid="share-link-input"
            />
            <button
              onClick={handleCopyLink}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                copied ? 'bg-green-100 text-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              data-testid="copy-share-link-btn"
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`https://x.com/intent/tweet?text=${encodeURIComponent(`Check out this craft design: ${suggestion.title}`)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="share-twitter-btn"
            >
              Share on X
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="share-facebook-btn"
            >
              Facebook
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent(suggestion.title)}&body=${encodeURIComponent(`Check out this craft design I made with Kura Stash!\n\n${shareUrl}`)}`}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="share-email-btn"
            >
              Email
            </a>
            <button
              onClick={handleUnshare}
              className="text-xs text-red-500 hover:text-red-700 hover:underline ml-auto"
              data-testid="unshare-btn"
            >
              Stop Sharing
            </button>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="px-5 pb-4">
        <p className="text-sm text-gray-soft leading-relaxed">{suggestion.description}</p>
      </div>

      {/* Color scheme */}
      {hasColors && (
        <div className="px-5 pb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-soft uppercase tracking-wide">Palette</span>
            <div className="flex gap-1.5">
              {suggestion.color_scheme.map((color, idx) => (
                <div
                  key={idx}
                  className="w-7 h-7 rounded-full border border-gray-200 shadow-sm"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Items needed */}
      {hasItems && (
        <div className="px-5 pb-4">
          <h4 className="text-xs text-gray-soft uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Package size={14} className="text-sage" />
            Items Needed
          </h4>
          <div className="space-y-1.5">
            {suggestion.items_needed.map((item, idx) => {
              const stashItem = item.in_stash && item.item_id ? stashItemMap?.[item.item_id] : null;
              const isClickable = item.in_stash && stashItem;
              const displayName = item.name || stashItem?.name;

              return (
                <div
                  key={idx}
                  onClick={() => isClickable && onItemClick?.(stashItem)}
                  className={`flex items-center gap-3 p-2.5 rounded-lg transition-all text-sm ${
                    item.in_stash
                      ? 'bg-green-50 border border-green-100'
                      : 'bg-amber-50 border border-amber-100'
                  } ${isClickable ? 'cursor-pointer hover:ring-1 hover:ring-sage/40' : ''}`}
                  data-testid={`project-item-${idx}`}
                >
                  {stashItem?.image_url ? (
                    <img
                      src={resolveImageUrl(stashItem.image_url)}
                      alt={displayName}
                      className="w-9 h-9 rounded-md object-cover flex-shrink-0 border border-green-200"
                    />
                  ) : (
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                      item.in_stash ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {item.in_stash ? (
                        <Check size={16} className="text-green-600" />
                      ) : (
                        <Package size={16} className="text-amber-600" />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-charcoal text-sm leading-tight truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-soft truncate">{item.description}</p>
                    {!item.in_stash && item.curated_addition_note && (
                      <p className="text-xs text-amber-700 mt-1 italic">
                        {item.curated_addition_note}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    item.in_stash
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {item.in_stash ? 'In Stash' : 'Get This'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Assembly instructions (collapsible) */}
      {hasInstructions && (
        <div className="px-5 pb-4">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center justify-between w-full text-left group"
            data-testid="toggle-instructions"
          >
            <span className="text-xs text-gray-soft uppercase tracking-wide">
              Assembly Instructions ({suggestion.assembly_instructions.length} steps)
            </span>
            {showInstructions ? (
              <ChevronUp size={16} className="text-gray-soft" />
            ) : (
              <ChevronDown size={16} className="text-gray-soft" />
            )}
          </button>
          {showInstructions && (
            <ol className="mt-2.5 space-y-2">
              {suggestion.assembly_instructions.map((step, idx) => (
                <li key={idx} className="flex gap-2.5 text-sm text-gray-soft">
                  <span className="flex-shrink-0 w-5 h-5 bg-sage-light text-sage rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
                    {idx + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Video recommendations */}
      {hasVideos && (
        <div className="px-5 pb-4">
          <h4 className="text-xs text-gray-soft uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-red-500">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
              <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
            </svg>
            Expert References
          </h4>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
            {suggestion.video_recommendations.map((video, idx) => (
              <VideoCard key={video.video_id || idx} video={video} />
            ))}
          </div>
        </div>
      )}

      {/* Finished photo section */}
      {hasFinishedPhoto && (
        <div className="px-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs text-gray-soft uppercase tracking-wide flex items-center gap-1.5">
              <Camera size={14} className="text-amber-500" />
              My Finished Creation
            </h4>
            <button
              onClick={handleRevertToIdea}
              className="text-[10px] text-gray-light hover:text-red-500 transition-colors"
              data-testid="revert-to-idea-btn"
            >
              Remove Photo
            </button>
          </div>
          <div className="rounded-lg overflow-hidden bg-gray-100 border border-amber-200">
            <img
              src={resolveImageUrl(suggestion.finished_photo_url)}
              alt="My finished creation"
              className="w-full max-h-56 object-contain"
              data-testid="finished-photo-img"
            />
          </div>
        </div>
      )}

      {/* Mark as Finished button (show only for idea/saved designs without a finished photo) */}
      {!hasFinishedPhoto && suggestion.saved && (
        <div className="px-5 pb-4">
          <button
            onClick={() => finishedPhotoRef.current?.click()}
            disabled={uploadingPhoto}
            className="w-full py-3 border-2 border-dashed border-amber-200 rounded-lg hover:border-amber-400 transition-colors flex items-center justify-center gap-2 group disabled:opacity-50"
            data-testid="mark-finished-btn"
          >
            {uploadingPhoto ? (
              <>
                <Loader2 size={16} className="text-amber-500 animate-spin" />
                <span className="text-sm text-gray-soft">Uploading photo...</span>
              </>
            ) : (
              <>
                <Award size={16} className="text-amber-500 group-hover:scale-110 transition-transform" />
                <span className="text-sm text-charcoal font-medium">I Made This — Upload Photo</span>
              </>
            )}
          </button>
          <input
            ref={finishedPhotoRef}
            type="file"
            accept="image/*"
            onChange={handleFinishedPhotoUpload}
            className="hidden"
            data-testid="finished-photo-input"
          />
        </div>
      )}

      {/* Visualize / Image section - hidden until feature is more useful */}
      {hasImage && (
      <div className="px-5 pb-5">
          <div className="relative rounded-lg overflow-hidden bg-gray-100">
            <img
              src={resolveImageUrl(suggestion.inspiration_image_url)}
              alt="Design visualization"
              className="w-full max-h-56 object-contain"
            />
          </div>
      </div>
      )}
    </div>
  );
};

export default ProjectCard;
